import image from "../../Images/CustomVpc.png"
import "../ContainerWebApp/ContainerWebApp.css"
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {oneDark} from "react-syntax-highlighter/dist/esm/styles/prism";
import ReactMarkdown from "react-markdown";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const CustomVpc = () => {
    const [activeSection, setActiveSection] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
            const handleScroll = () => {
                const sections = document.querySelectorAll("[data-section]");
                let current = "";
    
                sections.forEach((section) => {
                    const sectionTop = section.offsetTop;
                    const sectionHeight = section.clientHeight;
                    if (window.pageYOffset >= sectionTop - 100) {
                        current = section.getAttribute("data-section");
                    }
                });
    
                setActiveSection(current);
            };
    
            window.addEventListener("scroll", handleScroll);
            return () => window.removeEventListener("scroll", handleScroll);
        }, []);
    
        const scrollToSection = (sectionId) => {
            const element = document.querySelector(`[data-section="${sectionId}"]`);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        };

    return (
        <div className="documentation-layout">
            <aside className="sidebar">
                <nav className="sidebar-nav">
                  <button className="back-button" onClick={() => navigate('/')}>
                        ← Back
                    </button>
                    <h3 className="sidebar-title">Contents</h3>
                    <ul className="sidebar-list">

                        <li>
                            <a 
                                className={activeSection === "architecture" ? "active" : ""}
                                onClick={() => scrollToSection("architecture")}
                            >
                                Architecture
                            </a>
                        </li>
                            <a 
                                className={activeSection === "details" ? "active" : ""}
                                onClick={() => scrollToSection("details")}
                            >
                                Details
                            </a>
                        <li>
                            <a 
                                className={activeSection === "instantiation-code" ? "active" : ""}
                                onClick={() => scrollToSection("instantiation-code")}
                            >
                                Instantiation
                            </a>
                        </li>
                        <li>
                            <a 
                                className={activeSection === "stack-code" ? "active" : ""}
                                onClick={() => scrollToSection("stack-code")}
                            >
                                Stack Code
                            </a>
                        </li>
                        <li>
                            <a 
                                className={activeSection === "implementation-details" ? "active" : ""}
                                onClick={() => scrollToSection("implementation-details")}
                            >
                                Implementation Details
                            </a>
                        </li>
                    </ul>
                </nav>
            </aside>
            <main className="documentation-page">
                <div className="title-container" data-section="title">
                    <ReactMarkdown>
                        {`# Custom VPC Stack`}
                    </ReactMarkdown>
                </div>
                <div className="image-container" data-section="architecture">
                    <img className="image" src={image} />
                </div>
                <div className="markdown-left" data-section="details">
                    <ReactMarkdown>
                        {`
## Architecture
- **VPC**: Virtual Private Cloud with customizable CIDR block spanning multiple availability zones
- **Availability Zones**: Distributes subnets across AZs based on configuration for fault tolerance
- **Public Subnets**: Internet-accessible subnets with Internet Gateway routes for load balancers, bastion hosts, NAT Gateways
- **Private Subnets with Egress**: Subnets with NAT Gateway routes for application servers needing outbound internet access
- **Private Isolated Subnets**: Fully isolated subnets with no internet access for databases and sensitive workloads
- **Internet Gateway**: Enables direct internet connectivity for public subnets
- **NAT Gateways**: Provide outbound internet access for private subnets (1-3 gateways depending on configuration)
- **VPC Endpoints**: Optional private connections to AWS services (S3, DynamoDB, ECR, Secrets Manager, CloudWatch) bypassing internet
- **VPN Gateway**: Optional IPsec VPN for hybrid connectivity to on-premises networks

## Use Cases
- Foundation for multi-tier application architectures (web, app, database layers)
- Secure environments requiring network isolation and segmentation
- Hybrid cloud deployments connecting AWS to on-premises data centers via VPN
- Compliance-driven workloads requiring private connectivity to AWS services
- Multi-application hosting with shared networking infrastructure
- Development, staging, and production environment separation

## Cost
- **VPC & Subnets**: Free (no charge for VPC, subnets, route tables, or Internet Gateway)
- **NAT Gateway**: ~$32/month per NAT Gateway
- **VPN Gateway**: ~$36/month per VPN connection (if enabled)
- **VPC Flow Logs**: ~$0.50/GB ingested + CloudWatch storage costs
- **Interface VPC Endpoints**: ~$7.20/month per endpoint
- **Gateway VPC Endpoints (S3, DynamoDB)**: Free
                        `}
                    </ReactMarkdown>
                </div>
<div className="code-title-container" data-section="instantiation-code">
                    <ReactMarkdown>
                        {`# app.ts`}
                    </ReactMarkdown>
                </div>

                    <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
#!/usr/bin/env node
import * as cdk from 'aws-cdk-lib/core';
import { CustomVpcStack } from '../lib/custom_vpc_stack';

const app = new cdk.App();



new CustomVpcStack(app, "CustomVpc", {
  vpcCidr: "10.0.0.0/16",
  maxAzs: 3,
  natGateways: 3,
  enableVpcFlowLogs: true,
  enablePrivateLink: true,
  vpcEndpoints: {
    s3: true,
    dynamodb: true,
    ecr: true
  },
  subnetConfigurations: [
    {
      name: "LoadBalancer",
      subnetType: "PUBLIC",
      cidrMask: 28,
    },
    {
      name: "Application",
      subnetType: "PRIVATE_WITH_EGRESS",
      cidrMask: 24,
    },
    {
      name: "Database",
      subnetType: "PRIVATE_ISOLATED",
      cidrMask: 26,
    }
  ],
  env: { 
    account: process.env.CDK_DEFAULT_ACCOUNT, 
    region: process.env.CDK_DEFAULT_REGION 
  },
})
`}
                    </SyntaxHighlighter>
                </div>
                    
                <div className="code-title-container" data-section="stack-code">
                    <ReactMarkdown>
                        {`# Custom-VPC-stack.ts`}
                    </ReactMarkdown>
                </div>

                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
                        {`
import * as cdk from 'aws-cdk-lib/core';
import { Construct } from 'constructs';
import * as ec2 from "aws-cdk-lib/aws-ec2";
import * as logs from "aws-cdk-lib/aws-logs";
import * as iam from "aws-cdk-lib/aws-iam";

interface SubnetConfiguration {
  name: string;
  subnetType: "PUBLIC" | "PRIVATE_WITH_EGRESS" | "PRIVATE_ISOLATED";
  cidrMask: number;
  availabilityZones?: number;
}

interface CustomVpcStackProps extends cdk.StackProps {
  vpcName?: string;
  vpcCidr: string;
  maxAzs?: number;
  natGateways?: number;
  subnetConfigurations?: SubnetConfiguration[];
  enableDnsHostNames?: boolean;
  enableDnsSupport?: boolean;

  enablePrivateLink?: boolean;
  enableVpnGateway?: boolean;
  vpcEndpoints?: {
    s3?: boolean;
    dynamodb?: boolean;
    ec2?: boolean;
    ecr?: boolean;
    secretsManager?: boolean;
    cloudwatch?: boolean;
  }
  enableVpcFlowLogs?: boolean;
  flowLogsRetention?: number;
  createNetworkAcls?: boolean;
}

export class CustomVpcStack extends cdk.Stack {
  public readonly vpc: ec2.Vpc;
  public readonly vpcId: string;

  constructor(scope: Construct, id: string, props: CustomVpcStackProps) {
    super(scope, id, props);

    const defaultSubnetConfig: SubnetConfiguration[] = [
      {
        name: "Public",
        subnetType: 'PUBLIC',
        cidrMask: 24,
      },
      {
        name: "Private",
        subnetType: "PRIVATE_WITH_EGRESS",
        cidrMask: 24,
      },
      {
        name: "isolated",
        subnetType: "PRIVATE_ISOLATED",
        cidrMask: 28,
      }

    ]

    const subnetConfigs = props.subnetConfigurations || defaultSubnetConfig;

   

    const subnetConfiguration = subnetConfigs.map(config => ({
      name: config.name,
      subnetType: this.mapSubnetType(config.subnetType),
      cidrMask: config.cidrMask,
    }))

    this.vpc = new ec2.Vpc(this, "CustomVpc", {
      vpcName: props.vpcName,
      ipAddresses: ec2.IpAddresses.cidr(props.vpcCidr),
      maxAzs: props.maxAzs || 2,
      natGateways: props.natGateways ?? 1,
      subnetConfiguration: subnetConfiguration,
      enableDnsHostnames: props.enableDnsHostNames ?? true,
      enableDnsSupport: props.enableDnsSupport ?? true,
    })

    this.vpcId = this.vpc.vpcId;

    if (props.enablePrivateLink && props.vpcEndpoints) {
      if(props.vpcEndpoints.s3) {
        this.vpc.addGatewayEndpoint("S3Endpoint", {
          service: ec2.GatewayVpcEndpointAwsService.S3,
        })
      }
      if(props.vpcEndpoints.dynamodb) {
        this.vpc.addGatewayEndpoint("DynamoEndpoint", {
          service: ec2.GatewayVpcEndpointAwsService.DYNAMODB,
        })
      }
      if(props.vpcEndpoints.ec2) {
        this.vpc.addInterfaceEndpoint("EC2Endpoint", {
          service: ec2.InterfaceVpcEndpointAwsService.EC2,
        })
      }
      if(props.vpcEndpoints.ecr) {
        this.vpc.addInterfaceEndpoint("ECREndpoint", {
          service: ec2.InterfaceVpcEndpointAwsService.ECR,
        })
        this.vpc.addInterfaceEndpoint("ECRDockerEndpoint", {
          service: ec2.InterfaceVpcEndpointAwsService.ECR_DOCKER,
        })
      }
      if(props.vpcEndpoints.secretsManager) {
        this.vpc.addInterfaceEndpoint("SecretesManagerEndpoint", {
          service: ec2.InterfaceVpcEndpointAwsService.SECRETS_MANAGER,
        })
      }
      if(props.vpcEndpoints.cloudwatch) {
        this.vpc.addInterfaceEndpoint("CloudwatchEndpoint", {
          service: ec2.InterfaceVpcEndpointAwsService.CLOUDWATCH_LOGS,
        })
      }

    }
    if (props.enableVpnGateway) {
      const vpnGateway = new ec2.CfnVPNGateway(this, "VpnGateway", {
        type: "ipsec.1",
        amazonSideAsn: 64512
      })

      new ec2.CfnVPCGatewayAttachment(this, "VpnGatewayAttachment", {
        vpcId: this.vpc.vpcId,
        vpnGatewayId: vpnGateway.ref,
      })

      this.vpc.privateSubnets.forEach((subnet, index) => {
        new ec2.CfnVPNGatewayRoutePropagation(this, ${"VpnRouteProp${index}"}, {
          routeTableIds: [subnet.routeTable.routeTableId],
          vpnGatewayId: vpnGateway.ref,
        })
      })

    }

    if (props.enableVpcFlowLogs) {
      const logGroup = new logs.LogGroup(this, "VpcFlowLogsGroup", {
        retention: props.flowLogsRetention || logs.RetentionDays.ONE_WEEK,
        removalPolicy: cdk.RemovalPolicy.DESTROY,
      })

      const role = new iam.Role(this, "FlowLogsRole", {
        assumedBy: new iam.ServicePrincipal("vpc-flow-logs.amazonaws.com"),
      })

      new ec2.CfnFlowLog(this, "VpcFLowLog", {
        resourceId: this.vpc.vpcId,
        resourceType: "VPC",
        trafficType: "ALL",
        logDestinationType: "cloud-watch-logs",
        logGroupName: logGroup.logGroupName,
        deliverLogsPermissionArn: role.roleArn,
      })
      
      logGroup.grantWrite(role);

      

    }
    new cdk.CfnOutput(this, "VpcId", {
      value: this.vpcId,
      description: 'VPC ID',
      exportName: ${"${id}-VpcId"},
    })

    new cdk.CfnOutput(this, 'VpcCidr', {
      value: this.vpc.vpcCidrBlock,
      description: 'VPC CIDR block',
    });

    new cdk.CfnOutput(this, 'AvailabilityZones', {
      value: this.vpc.availabilityZones.join(', '),
      description: 'Availability Zones used',
    });

    new cdk.CfnOutput(this, 'PublicSubnets', {
      value: this.vpc.publicSubnets.map(s => s.subnetId).join(', '),
      description: 'Public subnet IDs',
    });

    new cdk.CfnOutput(this, 'PrivateSubnets', {
      value: this.vpc.privateSubnets.map(s => s.subnetId).join(', '),
      description: 'Private subnet IDs',
    });


  }

   private mapSubnetType(type: string): ec2.SubnetType {
      switch (type) {
        case "PUBLIC":
          return ec2.SubnetType.PUBLIC;
        case "PRIVATE_WITH_EGRESS":
          return ec2.SubnetType.PRIVATE_WITH_EGRESS;
        case "PRIVATE_ISOLATED":
          return ec2.SubnetType.PRIVATE_ISOLATED;
        default:
          return ec2.SubnetType.PRIVATE_WITH_EGRESS;
      }
    }

}
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="code-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Implementation Details`}
                    </ReactMarkdown>
                </div>
<div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Import Statements & Stack Props`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
import * as cdk from 'aws-cdk-lib/core';
import { Construct } from 'constructs';
import * as ec2 from "aws-cdk-lib/aws-ec2";
import * as logs from "aws-cdk-lib/aws-logs";
import * as iam from "aws-cdk-lib/aws-iam";

interface SubnetConfiguration {
  name: string;
  subnetType: "PUBLIC" | "PRIVATE_WITH_EGRESS" | "PRIVATE_ISOLATED";
  cidrMask: number;
  availabilityZones?: number;
}

interface CustomVpcStackProps extends cdk.StackProps {
  vpcName?: string;
  vpcCidr: string;
  maxAzs?: number;
  natGateways?: number;
  subnetConfigurations?: SubnetConfiguration[];
  enableDnsHostNames?: boolean;
  enableDnsSupport?: boolean;

  enablePrivateLink?: boolean;
  enableVpnGateway?: boolean;
  vpcEndpoints?: {
    s3?: boolean;
    dynamodb?: boolean;
    ec2?: boolean;
    ecr?: boolean;
    secretsManager?: boolean;
    cloudwatch?: boolean;
  }
  enableVpcFlowLogs?: boolean;
  flowLogsRetention?: number;
  createNetworkAcls?: boolean;
}
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Imports
- **aws-cdk-lib/core** - Core CDK constructs for defining stacks, apps, and basic infrastructure components
- **constructs** - Base Construct class that all CDK constructs extend from
- **aws-ec2** - EC2 and VPC constructs for creating virtual networks, subnets, security groups, and network resources
- **aws-logs** - CloudWatch Logs constructs for VPC flow logs and log retention management
- **aws-iam** - IAM constructs for managing permissions required by VPC endpoints and flow logs
## Subnet Interface
- **name** - Descriptive name for the subnet group (e.g., "Public", "Application", "Database")
- **subnetType** - Type of subnet: PUBLIC (internet access via IGW), PRIVATE_WITH_EGRESS (internet via NAT), or PRIVATE_ISOLATED (no internet)
- **cidrMask** - Subnet size in CIDR notation (e.g., 24 = /24 = 256 IPs, 26 = /26 = 64 IPs)
- **availabilityZones** - Optional number of AZs to create this subnet type in
## VPC Props
- **vpcName** -  custom name for the VPC resource
- **vpcCidr** -  VPC IP address range in CIDR notation (e.g., "10.0.0.0/16")
- **maxAzs** -  maximum number of Availability Zones to use for high availability
- **natGateways** -  number of NAT Gateways for private subnet internet access (affects cost and redundancy)
- **subnetConfigurations** -  array of custom subnet configurations for granular network design
- **enableDnsHostNames** -  flag to enable DNS hostname assignment for EC2 instances
- **enableDnsSupport** -  flag to enable Amazon-provided DNS resolution within the VPC
- **enablePrivateLink** -  flag to enable AWS PrivateLink for private connectivity to AWS services
- **enableVpnGateway** -  flag to attach a VPN gateway for site-to-site VPN connections
- **vpcEndpoints** -  object specifying which VPC endpoints to create for private AWS service access (S3, DynamoDB, EC2, ECR, Secrets Manager, CloudWatch)
- **enableVpcFlowLogs** -  flag to enable VPC Flow Logs for network traffic monitoring and security analysis
- **flowLogsRetention** -  number of days to retain flow logs in CloudWatch
- **createNetworkAcls** -  flag to create custom Network ACLs for subnet-level firewall rules


`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Constructor & Public Attributes`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
export class CustomVpcStack extends cdk.Stack {
  public readonly vpc: ec2.Vpc;
  public readonly vpcId: string;

  constructor(scope: Construct, id: string, props: CustomVpcStackProps) {
    super(scope, id, props);
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Constructor
- Takes a scope (parent construct), id (unique identifier), and optional props to configure the stack
- Calls super() to initialize the parent cdk.Stack class with the provided parameters
## Public Attributes
- **vpc** - Exposes the VPC resource as a public readonly property so other stacks can reference and use it
- **vpcId** - Exposes the VPC ID as a public readonly property for cross-stack references and external integrations
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Subnet Configutations`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
   const defaultSubnetConfig: SubnetConfiguration[] = [
      {
        name: "Public",
        subnetType: 'PUBLIC',
        cidrMask: 24,
      },
      {
        name: "Private",
        subnetType: "PRIVATE_WITH_EGRESS",
        cidrMask: 24,
      },
      {
        name: "isolated",
        subnetType: "PRIVATE_ISOLATED",
        cidrMask: 28,
      }

    ]

    const subnetConfigs = props.subnetConfigurations || defaultSubnetConfig;

   

    const subnetConfiguration = subnetConfigs.map(config => ({
      name: config.name,
      subnetType: this.mapSubnetType(config.subnetType),
      cidrMask: config.cidrMask,
    }))
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Configuration 
- Provides a sensible default three-tier network architecture if custom subnets aren't specified
- **subnetConfigs** - Uses custom subnet configurations from props if provided, otherwise falls back to the default configuration
- Maps the string subnet types ("PUBLIC", "PRIVATE_WITH_EGRESS", "PRIVATE_ISOLATED") to CDK's ec2.SubnetType enum values
- Creates the final subnet configuration array that CDK's VPC construct can consume
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# VPC Setup`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
   this.vpc = new ec2.Vpc(this, "CustomVpc", {
      vpcName: props.vpcName,
      ipAddresses: ec2.IpAddresses.cidr(props.vpcCidr),
      maxAzs: props.maxAzs || 2,
      natGateways: props.natGateways ?? 1,
      subnetConfiguration: subnetConfiguration,
      enableDnsHostnames: props.enableDnsHostNames ?? true,
      enableDnsSupport: props.enableDnsSupport ?? true,
    })

    this.vpcId = this.vpc.vpcId;
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
- Creates the VPC and assigns it to the public vpc property for cross-stack references
- **vpcName** - Sets a custom name for the VPC if provided in props
- **ipAddresses** - Defines the VPC's IP address range using the CIDR block from props (e.g., "10.0.0.0/16")
- **maxAzs** - Distributes subnets across 2 Availability Zones by default, or uses custom value from props for high availability
- **natGateways** - Creates 1 NAT Gateway by default, or uses custom count from props
- **subnetConfiguration** - Uses the mapped subnet configuration array created earlier (custom or default)
- **enableDnsHostnames** - Enables DNS hostnames by default (true) so EC2 instances get public DNS names
- **enableDnsSupport** - Enables Amazon-provided DNS resolution by default (true) for name resolution within the VPC
- Extracts and stores the VPC ID in the public vpcId property for easy reference by other resources and stacks
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Endpoints`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
   if (props.enablePrivateLink && props.vpcEndpoints) {
      if(props.vpcEndpoints.s3) {
        this.vpc.addGatewayEndpoint("S3Endpoint", {
          service: ec2.GatewayVpcEndpointAwsService.S3,
        })
      }
      if(props.vpcEndpoints.dynamodb) {
        this.vpc.addGatewayEndpoint("DynamoEndpoint", {
          service: ec2.GatewayVpcEndpointAwsService.DYNAMODB,
        })
      }
      if(props.vpcEndpoints.ec2) {
        this.vpc.addInterfaceEndpoint("EC2Endpoint", {
          service: ec2.InterfaceVpcEndpointAwsService.EC2,
        })
      }
      if(props.vpcEndpoints.ecr) {
        this.vpc.addInterfaceEndpoint("ECREndpoint", {
          service: ec2.InterfaceVpcEndpointAwsService.ECR,
        })
        this.vpc.addInterfaceEndpoint("ECRDockerEndpoint", {
          service: ec2.InterfaceVpcEndpointAwsService.ECR_DOCKER,
        })
      }
      if(props.vpcEndpoints.secretsManager) {
        this.vpc.addInterfaceEndpoint("SecretesManagerEndpoint", {
          service: ec2.InterfaceVpcEndpointAwsService.SECRETS_MANAGER,
        })
      }
      if(props.vpcEndpoints.cloudwatch) {
        this.vpc.addInterfaceEndpoint("CloudwatchEndpoint", {
          service: ec2.InterfaceVpcEndpointAwsService.CLOUDWATCH_LOGS,
        })
      }
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Gateway Endpoints (Free)
- **S3 Endpoint** - Enables private access to S3 buckets without internet gateway or NAT Gateway traffic
- **DynamoDB Endpoint** - Enables private access to DynamoDB tables without internet connectivity
- Gateway endpoints are free and route traffic through the VPC's route tables
## Interface Endpoints (Hourly Charge)
- **EC2 Endpoint** - Enables private API calls to EC2 service for instance management and metadata
- **ECR Endpoints** - Creates two endpoints (ECR API and ECR Docker) required for pulling container images from Elastic Container Registry privately
- **Secrets Manager Endpoint** - Enables private access to retrieve secrets without internet traffic
- **CloudWatch Logs Endpoint** - Enables private log publishing to CloudWatch from isolated subnets

`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# VPN Gateway`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
   if (props.enableVpnGateway) {
      const vpnGateway = new ec2.CfnVPNGateway(this, "VpnGateway", {
        type: "ipsec.1",
        amazonSideAsn: 64512
      })

      new ec2.CfnVPCGatewayAttachment(this, "VpnGatewayAttachment", {
        vpcId: this.vpc.vpcId,
        vpnGatewayId: vpnGateway.ref,
      })

      this.vpc.privateSubnets.forEach((subnet, index) => {
        new ec2.CfnVPNGatewayRoutePropagation(this, ${"VpnRouteProp${index}"}, {
          routeTableIds: [subnet.routeTable.routeTableId],
          vpnGatewayId: vpnGateway.ref,
        })
      })

    }
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## VPN Gateway
- **type: "ipsec.1"** - Uses IPsec protocol for secure site-to-site VPN connections
- **amazonSideAsn: 64512** - Sets the Autonomous System Number for BGP routing (64512 is in the private ASN range)
- Attaches the VPN Gateway to the VPC using CfnVPCGatewayAttachment
- Links the gateway's ID to the VPC ID to establish the connection
## Route Propogation
- Iterates through all private subnets in the VPC
- Creates route propagation for each private subnet's route table
- VpnGatewayRoutePropagation - Automatically adds routes learned via BGP from the VPN connection to subnet route tables

`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# VPC Flow Logs`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
   if (props.enableVpcFlowLogs) {
      const logGroup = new logs.LogGroup(this, "VpcFlowLogsGroup", {
        retention: props.flowLogsRetention || logs.RetentionDays.ONE_WEEK,
        removalPolicy: cdk.RemovalPolicy.DESTROY,
      })

      const role = new iam.Role(this, "FlowLogsRole", {
        assumedBy: new iam.ServicePrincipal("vpc-flow-logs.amazonaws.com"),
      })

      new ec2.CfnFlowLog(this, "VpcFLowLog", {
        resourceId: this.vpc.vpcId,
        resourceType: "VPC",
        trafficType: "ALL",
        logDestinationType: "cloud-watch-logs",
        logGroupName: logGroup.logGroupName,
        deliverLogsPermissionArn: role.roleArn,
      })
      
      logGroup.grantWrite(role);

      

    }
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Flow logs
- Captures information about IP traffic going to and from network interfaces in the VPC for security analysis and troubleshooting
## Cloudwatch Log Group
- Creates a dedicated log group to store flow log data
- **retention** - Retains logs for 1 week by default, or uses custom retention period from props (controls storage costs)
- **removalPolicy: DESTROY** - Automatically deletes the log group when the stack is destroyed
## IAM Role
- Creates an IAM role that VPC Flow Logs service assumes to write logs to CloudWatch
- **assumedBy: ServicePrincipal** - Grants the VPC Flow Logs service permission to assume this role
## Flow Log Configuration
- **resourceId** - Monitors the entire VPC (could also be configured for specific subnets or network interfaces)
- **resourceType: "VPC"** - Specifies that this flow log applies to the VPC level
- **trafficType: "ALL"** - Captures all traffic (accepted, rejected, and all); alternatives are "ACCEPT" or "REJECT" only
- **logDestinationType: "cloud-watch-logs"** - Sends logs to CloudWatch (alternatives include S3 or Kinesis Data Firehose)
- **logGroupName** - Specifies where to write the logs
- **deliverLogsPermissionArn** - Provides the IAM role ARN for write permissions
- **grantWrite(role)** - Grants the IAM role permission to write log events to the CloudWatch log group
`}
                    </ReactMarkdown>
                    </div>

                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Outputs`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
    new cdk.CfnOutput(this, "VpcId", {
      value: this.vpcId,
      description: 'VPC ID',
      exportName: ${"${id}-VpcId"},
    })

    new cdk.CfnOutput(this, 'VpcCidr', {
      value: this.vpc.vpcCidrBlock,
      description: 'VPC CIDR block',
    });

    new cdk.CfnOutput(this, 'AvailabilityZones', {
      value: this.vpc.availabilityZones.join(', '),
      description: 'Availability Zones used',
    });

    new cdk.CfnOutput(this, 'PublicSubnets', {
      value: this.vpc.publicSubnets.map(s => s.subnetId).join(', '),
      description: 'Public subnet IDs',
    });

    new cdk.CfnOutput(this, 'PrivateSubnets', {
      value: this.vpc.privateSubnets.map(s => s.subnetId).join(', '),
      description: 'Private subnet IDs',
    });

  
  }
}
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
## Outputs
- These outputs display important values in the terminal after deployment and can be referenced by other stacks or automation tools
`}
                    </ReactMarkdown>
                    </div>
                <div className="section-title-container" data-section="implementation-details">
                    <ReactMarkdown>
                        {`# Map Subnets Helper Function`}
                    </ReactMarkdown>
                </div>
                <div className="code-container">
                    <SyntaxHighlighter language="typescript" style={oneDark}>
{`
    private mapSubnetType(type: string): ec2.SubnetType {
      switch (type) {
        case "PUBLIC":
          return ec2.SubnetType.PUBLIC;
        case "PRIVATE_WITH_EGRESS":
          return ec2.SubnetType.PRIVATE_WITH_EGRESS;
        case "PRIVATE_ISOLATED":
          return ec2.SubnetType.PRIVATE_ISOLATED;
        default:
          return ec2.SubnetType.PRIVATE_WITH_EGRESS;
      }
    }
`}
                    </SyntaxHighlighter>
                    
                </div>
                <div className="details-markdown-left" data-section="implementation-details">
                        <ReactMarkdown>
{`
- Private helper method that converts string subnet type values to CDK's ec2.SubnetType enum constants
`}
                    </ReactMarkdown>
                    </div>
            </main>
        </div>
    )

}
export default CustomVpc;